import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import ContactForm, { ContactFormState } from './ContactForm';
import { GET_CONTACT_BY_ID_QUERY, GET_CONTACTS_QUERY } from '../../queries/ContactQueries';
import { UPDATE_CONTACT_MUTATION } from '../../mutations/ContactMutations';
import { type Contact, validate as validateContact } from '../../models/Contact';

function ContactEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<ContactFormState['values']>;
  onSubmit: (id: number, formValues: Partial<ContactFormState['values']>) => Promise<boolean>;
}) {
  const { contactId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [formState, setFormState] = useState<ContactFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ContactFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ContactFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ContactFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ContactFormState['values']>) => {
        const { issues } = validateContact(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateContact(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    const id = +(contactId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(id, formValues);

        if (success) {
          notifications.show('Contact edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/contacts/${id}`);
        } else {
          notifications.show(
            'Failed to edit contact. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit contact. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [contactId, formValues, navigate, notifications, onSubmit, setFormErrors]);

  return (
    <ContactForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/contacts/${contactId}`}
    />
  );
}

export default function ContactEdit() {
  const { contactId } = useParams();

  const [getContactById] = useLazyQuery(
    GET_CONTACT_BY_ID_QUERY);

  const [updateContact] = useMutation(
    UPDATE_CONTACT_MUTATION, {
    refetchQueries: [{ query: GET_CONTACTS_QUERY }, { query: GET_CONTACT_BY_ID_QUERY, variables: { id: +(contactId || '0') } }],
  });

  const [contact, setContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(contactId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getVendorByIdError } = await getContactById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getVendorByIdError) {
          setError(getVendorByIdError);
        }

        if (data?.contact) {
          setContact(data.contact);
        } else {
          setError({ message: 'No contact found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [contactId, getContactById]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<ContactFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateContact({
        variables: {
          id: id,
          input: {
            id: id,
            name: formValues.name || '',
            email: formValues.email || '',
            phone: formValues.phone || ''
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedContact) {
        setContact(result.updatedContact);
        return true;
      }

      return false;
    },
    [updateContact],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return contact ? (
      <ContactEditForm initialValues={contact} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, contact, handleSubmit]);

  return (
    <PageContainer
      title={`Edit contact ${contact?.name}`}
      breadcrumbs={[
        { title: 'Contacts', path: '/dashboard/contacts' },
        { title: `${contact?.name}`, path: `/dashboard/dashboard/contacts/${contactId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
